import { createContext, useContext, useState, useEffect, useCallback, useRef, useMemo, ReactNode } from 'react'
import type { BrandSettings } from '@digital-twins/shared-types'
import { usersApi, type UserProfile, type FeatureFlags } from '@/lib/api'
import { useAuth } from '@/lib/config/auth-provider'
import { useTheme as useAppTheme } from '@/lib/theme-provider'
import { hexToHSL } from '@/lib/theme/color'
import { isThemeFontId, isThemeHeadingFontId, isThemePresetId } from '@/lib/theme/theme-presets'
import { applyThemeBrandTokens, resetThemeBrandTokens } from '@digital-twins/deck-ui/foundations/theme/applyThemeBrandTokens'
import { getFairgenTwinsDefaultBrandSettings, isFairgenTwinsDefaultBrand, resolveLegacyFairgenLogoUrl } from '@/lib/theme/fairgen-default-brand'
import { BrandSaveCoordinator, applyCanonicalSkippingPending, mergeBrandPatch, type BrandPatch } from '@/lib/brand/brand-save-coordinator'

const BRAND_CACHE_KEY = 'brandSettings'

interface BrandRuntimeState {
  effectiveSnapshot: BrandSettings
  serverRevision: number | null
  localMutationEpoch: number
  pendingPatch: BrandPatch
  failedPatch: BrandPatch | null
  failedError: string | null
  isPersisting: boolean
}

interface BrandContextType {
  brandSettings: BrandSettings
  isLoading: boolean
  /**
   * True whenever a brand save is debounced, in flight, or there is an
   * unflushed pending patch. UI surfaces (Apply, Save, Detect, etc.) should
   * stay in their loading state while this is true.
   */
  isPersisting: boolean
  brandLogoUrl: string | null
  brandLogoUrlOnDark: string | null
  brandIconLogoUrl: string | null
  /**
   * The single public save entry point. Delegates to the coordinator which
   * owns coalescing, single-in-flight enforcement, revision tracking, deep
   * merge of `brandThemeConfig.seeds`, and stale-response suppression.
   *
   * Pass `{ immediate: true }` to bypass the debounce window (Apply, Save,
   * Detect actions). Returns the canonical server BrandSettings.
   */
  updateBrandSettings: (settings: Partial<BrandSettings>, opts?: { immediate?: boolean }) => Promise<BrandSettings>
  /** Runtime-only preview used by color pickers before an explicit save. */
  previewBrandSettings: (settings: BrandSettings) => void
  /** Restore the current effective provider snapshot after a runtime-only preview. */
  clearBrandSettingsPreview: () => void
  /** Cancel any debounced timer and await a final composed save. */
  flushBrandSettings: () => Promise<void>
  /** Last failed optimistic patch, surfaced so UI can offer a visible retry affordance. */
  failedBrandPatch: BrandPatch | null
  brandPersistenceError: string | null
  retryFailedBrandSettings: () => Promise<BrandSettings | null>
  resetToDefaults: () => Promise<void>
}

const BrandContext = createContext<BrandContextType | undefined>(undefined)

function createEmptyBrandSettings(): BrandSettings {
  return {
    brandPrimaryColor: null,
    brandSecondaryColor: null,
    brandThemeConfig: null,
    brandLogoUrl: null,
    brandLogoUrlOnDark: null,
    brandLogoNeedsInversion: null,
    brandLogoOnDarkNeedsInversion: null,
    brandIconLogoUrl: null,
    brandDomain: null,
    brandRoleGraph: null,
    brandRoleGraphVersion: null,
    brandRoleGraphHash: null,
    themePresetId: null,
    brandFontFamily: null,
    brandHeadingFontFamily: null,
    themeOverrides: null,
    shareFooterText: null,
  }
}

function createInitialRuntimeState(): BrandRuntimeState {
  return {
    effectiveSnapshot: createEmptyBrandSettings(),
    serverRevision: null,
    localMutationEpoch: 0,
    pendingPatch: {},
    failedPatch: null,
    failedError: null,
    isPersisting: false,
  }
}

function cacheBrandSettings(settings: BrandSettings) {
  if (typeof window === 'undefined') return
  try {
    localStorage.setItem(BRAND_CACHE_KEY, JSON.stringify(settings))
  } catch {
    // Ignore storage errors
  }
}

function clearBrandCache() {
  if (typeof window === 'undefined') return
  try {
    localStorage.removeItem(BRAND_CACHE_KEY)
  } catch {
    // Ignore errors
  }
}

function getBrandRevision(settings: Pick<Partial<BrandSettings>, 'brandRevision'>): number | null {
  return typeof settings.brandRevision === 'number' && Number.isFinite(settings.brandRevision)
    ? settings.brandRevision
    : null
}

function hasBrandPatchKeys(patch: BrandPatch): boolean {
  return Object.keys(patch).some((key) => (patch as Record<string, unknown>)[key] !== undefined)
}

function getErrorMessage(error: unknown): string {
  return error instanceof Error ? error.message : 'Failed to save brand settings'
}

function logStaleSnapshot(message: string, details: Record<string, unknown>) {
  if (!import.meta.env.DEV) return
  console.info(message, details)
}

function getUserBrandSnapshot(user: UserProfile): BrandSettings {
  const revision = getBrandRevision(user)
  const snapshot: BrandSettings = {
    brandPrimaryColor: user.brandPrimaryColor ?? null,
    brandSecondaryColor: user.brandSecondaryColor ?? null,
    brandThemeConfig: user.brandThemeConfig ?? null,
    brandLogoUrl: user.brandLogoUrl ?? null,
    brandLogoUrlOnDark: user.brandLogoUrlOnDark ?? null,
    // Server-measured; the browser cannot read these logos back out of a
    // canvas, because the bucket serving them sends no CORS header.
    brandLogoNeedsInversion: user.brandLogoNeedsInversion ?? null,
    brandLogoOnDarkNeedsInversion: user.brandLogoOnDarkNeedsInversion ?? null,
    brandIconLogoUrl: user.brandIconLogoUrl ?? null,
    brandDomain: user.brandDomain ?? null,
    brandRoleGraph: user.brandRoleGraph ?? null,
    brandRoleGraphVersion: user.brandRoleGraphVersion ?? null,
    brandRoleGraphHash: user.brandRoleGraphHash ?? null,
    themePresetId: isThemePresetId(user.themePresetId) ? user.themePresetId : null,
    brandFontFamily: isThemeFontId(user.brandFontFamily) ? user.brandFontFamily : null,
    brandHeadingFontFamily: isThemeHeadingFontId(user.brandHeadingFontFamily) ? user.brandHeadingFontFamily : null,
    themeOverrides: user.themeOverrides ?? null,
    shareFooterText: user.shareFooterText ?? null,
  }

  if (revision !== null) {
    snapshot.brandRevision = revision
  }

  return snapshot
}


function normalizeBrandSettings(settings: BrandSettings): BrandSettings {
  const normalized: BrandSettings = { ...settings }
  if (normalized.brandPrimaryColor?.startsWith('#')) {
    normalized.brandPrimaryColor = hexToHSL(normalized.brandPrimaryColor)
  }
  if (normalized.brandSecondaryColor?.startsWith('#')) {
    normalized.brandSecondaryColor = hexToHSL(normalized.brandSecondaryColor)
  }
  if (normalized.brandThemeConfig?.seeds) {
    const seeds = { ...normalized.brandThemeConfig.seeds }
    for (const [seedKey, seedPair] of Object.entries(seeds)) {
      if (!seedPair) continue
      seeds[seedKey as keyof typeof seeds] = {
        light: seedPair.light?.startsWith('#') ? hexToHSL(seedPair.light) : seedPair.light,
        dark: seedPair.dark?.startsWith('#') ? hexToHSL(seedPair.dark) : seedPair.dark,
      }
    }
    normalized.brandThemeConfig = {
      ...normalized.brandThemeConfig,
      seeds,
    }
  }
  return normalized
}

function normalizeBrandPatch(patch: BrandPatch): BrandPatch {
  const out: BrandPatch = { ...patch }
  if (typeof out.brandPrimaryColor === 'string' && out.brandPrimaryColor.startsWith('#')) {
    out.brandPrimaryColor = hexToHSL(out.brandPrimaryColor)
  }
  if (typeof out.brandSecondaryColor === 'string' && out.brandSecondaryColor.startsWith('#')) {
    out.brandSecondaryColor = hexToHSL(out.brandSecondaryColor)
  }
  if (out.brandThemeConfig && out.brandThemeConfig.seeds) {
    const seeds = { ...out.brandThemeConfig.seeds }
    for (const [seedKey, seedPair] of Object.entries(seeds)) {
      if (!seedPair) continue
      seeds[seedKey as keyof typeof seeds] = {
        light: seedPair.light?.startsWith('#') ? hexToHSL(seedPair.light) : seedPair.light,
        dark: seedPair.dark?.startsWith('#') ? hexToHSL(seedPair.dark) : seedPair.dark,
      }
    }
    out.brandThemeConfig = { ...out.brandThemeConfig, seeds }
  }
  return out
}

/**
 * Derive the brand token scope from the user's feature flags.
 *
 * - `'full'`  → `whiteLabel` is on: entire app chrome adopts the brand.
 * - `'deck'`  → `brandedTheme` is on: only deliverables (decks/slides/charts) are branded.
 * - `null`    → no branding flags are set; no tokens are applied.
 */
export function deriveBrandScope(
  featureFlags?: Pick<FeatureFlags, 'whiteLabel' | 'brandedTheme'> | null,
): 'full' | 'deck' | null {
  if (featureFlags?.whiteLabel) return 'full'
  if (featureFlags?.brandedTheme) return 'deck'
  return null
}

/** Imperative brand-color apply for previews and one-off render paths (e.g. CoverSlidePreview). */
export function applyBrandColors(
  settings: BrandSettings,
  resolvedTheme: 'light' | 'dark' = 'dark',
  opts?: { whiteLabelEnabled?: boolean; restore?: boolean },
) {
  // Legacy callers pass whiteLabelEnabled; treat false as deck scope.
  const scope = opts?.whiteLabelEnabled === false ? 'deck' : 'full'
  return applyThemeBrandTokens(normalizeBrandSettings(settings), resolvedTheme, {
    scope,
    restore: opts?.restore,
  })
}

/** Reset brand colors written by applyBrandColors. */
export function resetBrandColors(
  _resolvedTheme: 'light' | 'dark' = 'dark',
  opts?: { whiteLabelEnabled?: boolean },
) {
  resetThemeBrandTokens(opts?.whiteLabelEnabled === false ? 'deck' : 'full')
}

/**
 * True when the org has `whiteLabel` enabled (full scope) and non-default app branding is active.
 * Fairgen Twins defaults keep the foundation theme (no inline brand token overrides).
 *
 * NOTE: Only returns true for full-scope (whiteLabel) branding — deck-scope (brandedTheme)
 * does not constitute an "app branded experience" since the app shell stays Fairgen.
 */
export function isAppBrandedExperience(
  whiteLabelEnabled: boolean,
  settings: BrandSettings,
): boolean {
  return whiteLabelEnabled && !isFairgenTwinsDefaultBrand(settings)
}

function syncAppBrandedDocumentFlag(settings: BrandSettings, brandScope: 'full' | 'deck' | null) {
  if (typeof document === 'undefined') {
    return
  }
  // Only full-scope (whiteLabel) branding marks the document as app-branded.
  // deck-scope (brandedTheme) does not apply app-shell tokens.
  if (brandScope === 'full' && !isFairgenTwinsDefaultBrand(settings)) {
    document.documentElement.dataset.appBranded = 'true'
  } else {
    delete document.documentElement.dataset.appBranded
  }
}

function applyAuthenticatedBrandTokens(
  settings: BrandSettings,
  resolvedTheme: 'light' | 'dark',
  brandScope: 'full' | 'deck' | null,
) {
  syncAppBrandedDocumentFlag(settings, brandScope)

  if (!brandScope || (brandScope === 'full' && isFairgenTwinsDefaultBrand(settings))) {
    resetThemeBrandTokens('full')
    return
  }

  const normalized = normalizeBrandSettings(settings)
  applyThemeBrandTokens(normalized, resolvedTheme, { scope: brandScope })
}

export function BrandProvider({ children }: { children: ReactNode }) {
  const { user, isLoading: authLoading } = useAuth()
  const { resolvedTheme, setPresetId, setFontFamily, setHeadingFontFamily, setThemeOverrides } = useAppTheme()

  const [runtimeState, setRuntimeState] = useState<BrandRuntimeState>(() => createInitialRuntimeState())
  const effectiveSnapshot = runtimeState.effectiveSnapshot
  // Stored pre-refresh default lockups ("Fairgen Twins") render as the current Fairgen lockup.
  const brandSettings = useMemo<BrandSettings>(
    () => ({
      ...effectiveSnapshot,
      brandLogoUrl: resolveLegacyFairgenLogoUrl(effectiveSnapshot.brandLogoUrl),
      brandLogoUrlOnDark: resolveLegacyFairgenLogoUrl(effectiveSnapshot.brandLogoUrlOnDark),
    }),
    [effectiveSnapshot],
  )
  const [isLoading, setIsLoading] = useState(true)

  const runtimeStateRef = useRef<BrandRuntimeState>(runtimeState)
  /** Stable ref so the coordinator sees the latest brandSettings during canonical merge. */
  const brandSettingsRef = useRef<BrandSettings>(brandSettings)
  useEffect(() => {
    runtimeStateRef.current = runtimeState
    brandSettingsRef.current = brandSettings
  }, [runtimeState, brandSettings])
  /** Ref to latest user/theme/brandScope — coordinator callbacks must see fresh values. */
  const resolvedThemeRef = useRef(resolvedTheme)
  useEffect(() => {
    resolvedThemeRef.current = resolvedTheme
  }, [resolvedTheme])
  const brandScopeRef = useRef<'full' | 'deck' | null>(deriveBrandScope(user?.featureFlags))
  useEffect(() => {
    brandScopeRef.current = deriveBrandScope(user?.featureFlags)
  }, [user?.featureFlags?.whiteLabel, user?.featureFlags?.brandedTheme])
  const authIdentityRef = useRef<string | null | undefined>(undefined)
  const previousResolvedThemeRef = useRef(resolvedTheme)
  const lastApplyReasonRef = useRef<string | null>(null)
  const previewDisposerRef = useRef<(() => void) | null>(null)

  const syncThemeProviderFromBrandSettings = useCallback(
    (settings: BrandSettings | null, brandScope: 'full' | 'deck' | null) => {
      // ThemeProvider shell state (preset/font/overrides) only follows the brand when
      // full-scope (whiteLabel) is active. deck-scope branding targets deliverables only.
      if (!settings || brandScope !== 'full' || isFairgenTwinsDefaultBrand(settings)) {
        setPresetId(null)
        setFontFamily(null)
        setHeadingFontFamily(null)
        setThemeOverrides({})
        return
      }

      setPresetId(settings.themePresetId && isThemePresetId(settings.themePresetId) ? settings.themePresetId : null)
      setFontFamily(settings.brandFontFamily && isThemeFontId(settings.brandFontFamily) ? settings.brandFontFamily : null)
      setHeadingFontFamily(
        settings.brandHeadingFontFamily && isThemeHeadingFontId(settings.brandHeadingFontFamily)
          ? settings.brandHeadingFontFamily
          : null,
      )
      setThemeOverrides(settings.themeOverrides ?? {})
    },
    [setPresetId, setFontFamily, setHeadingFontFamily, setThemeOverrides],
  )

  const commitRuntimeState = useCallback((nextState: BrandRuntimeState) => {
    runtimeStateRef.current = nextState
    brandSettingsRef.current = nextState.effectiveSnapshot
    setRuntimeState(nextState)
  }, [])

  const runBrandSideEffects = useCallback(
    (snapshot: BrandSettings, reason: string) => {
      previewDisposerRef.current?.()
      previewDisposerRef.current = null
      lastApplyReasonRef.current = reason
      const normalized = normalizeBrandSettings(snapshot)

      cacheBrandSettings(normalized)
      syncThemeProviderFromBrandSettings(normalized, brandScopeRef.current)
      applyAuthenticatedBrandTokens(normalized, resolvedThemeRef.current, brandScopeRef.current)
    },
    [syncThemeProviderFromBrandSettings],
  )

  const previewBrandSettings = useCallback(
    (snapshot: BrandSettings) => {
      previewDisposerRef.current?.()
      previewDisposerRef.current = null

      const normalized = normalizeBrandSettings(snapshot)
      const brandScope = brandScopeRef.current
      // Full-scope previews also sync ThemeProvider shell state (preset/font/overrides).
      // deck-scope and no-scope previews only apply deliverable tokens.
      if (brandScope === 'full') {
        syncThemeProviderFromBrandSettings(normalized, 'full')
      }
      const disposer = applyThemeBrandTokens(normalized, resolvedThemeRef.current, {
        scope: brandScope ?? 'deck',
        restore: true,
      })
      previewDisposerRef.current = typeof disposer === 'function' ? disposer : null
    },
    [syncThemeProviderFromBrandSettings],
  )

  const clearBrandSettingsPreview = useCallback(() => {
    previewDisposerRef.current?.()
    previewDisposerRef.current = null
    const currentSnapshot = runtimeStateRef.current.effectiveSnapshot
    syncThemeProviderFromBrandSettings(currentSnapshot, brandScopeRef.current)
    applyAuthenticatedBrandTokens(currentSnapshot, resolvedThemeRef.current, brandScopeRef.current)
  }, [syncThemeProviderFromBrandSettings])

  const shouldApplyServerSnapshot = useCallback((incomingRevision: number | null, source: 'profile' | 'save') => {
    const currentState = runtimeStateRef.current
    const hasLocalWork =
      currentState.isPersisting ||
      currentState.localMutationEpoch > 0 ||
      hasBrandPatchKeys(currentState.pendingPatch)

    if (incomingRevision === null) {
      if (source === 'save' || !hasLocalWork) {
        return true
      }

      logStaleSnapshot('[BrandProvider] Suppressed unrevisioned brand profile during local mutation', {
        localMutationEpoch: currentState.localMutationEpoch,
      })
      return false
    }

    if (currentState.serverRevision === null || incomingRevision > currentState.serverRevision) {
      return true
    }

    if (incomingRevision === currentState.serverRevision) {
      if (source === 'save' || !hasLocalWork) {
        return true
      }

      logStaleSnapshot('[BrandProvider] Suppressed equal-revision brand profile during local mutation', {
        incomingRevision,
        localMutationEpoch: currentState.localMutationEpoch,
      })
      return false
    }

    logStaleSnapshot('[BrandProvider] Suppressed stale brand snapshot', {
      incomingRevision,
      serverRevision: currentState.serverRevision,
      source,
    })
    return false
  }, [])

  /** Ref to the coordinator itself so callbacks (defined at construction time)
   * can call `getPending()` on the current instance without a TDZ reference. */
  const coordinatorRef = useRef<BrandSaveCoordinator | null>(null)

  // Coordinator is a per-provider singleton — created once and reused.
  const coordinator = useMemo(() => {
    const instance = new BrandSaveCoordinator({
      send: async (patch) => {
        const normalized = normalizeBrandPatch(patch)
        return usersApi.updateBrandSettings(normalized)
      },
      onCanonical: (canonical, appliedPatch) => {
        // Merge canonical state field-by-field, skipping keys still present
        // in the coordinator's pending patch. New enqueues that arrived
        // between firing this request and its response landing are already
        // back in `pending`; without skipping them we would briefly clobber
        // the user's in-progress edits with stale canonical values until the
        // next composed save lands.
        const livePending = coordinatorRef.current?.getPending() ?? {}
        const normalizedCanonical = normalizeBrandSettings(canonical)
        const canonicalRevision = getBrandRevision(normalizedCanonical)
        if (!shouldApplyServerSnapshot(canonicalRevision, 'save')) {
          return
        }

        const next = normalizeBrandSettings(
          applyCanonicalSkippingPending(brandSettingsRef.current, normalizedCanonical, livePending),
        )
        const currentState = runtimeStateRef.current
        const hasLivePending = hasBrandPatchKeys(livePending)
        commitRuntimeState({
          ...currentState,
          effectiveSnapshot: next,
          serverRevision: canonicalRevision ?? currentState.serverRevision,
          localMutationEpoch: hasLivePending ? currentState.localMutationEpoch : 0,
          pendingPatch: livePending,
          failedPatch: null,
          failedError: null,
        })
        void appliedPatch
        runBrandSideEffects(next, 'save-canonical')
      },
      onError: (error, attemptedPatch) => {
        // Log to console (visible during development) and let any configured
        // error boundary / reporting tool capture it from the unhandled-error stream.
        console.error('[BrandProvider] Failed to update brand settings:', error)
        const currentState = runtimeStateRef.current
        commitRuntimeState({
          ...currentState,
          failedPatch: attemptedPatch,
          failedError: getErrorMessage(error),
          isPersisting: coordinatorRef.current?.getState().isPersisting ?? false,
        })
      },
    })
    coordinatorRef.current = instance
    return instance
  }, [commitRuntimeState, runBrandSideEffects, shouldApplyServerSnapshot])

  useEffect(() => {
    const unsubscribe = coordinator.subscribe((state) => {
      const currentState = runtimeStateRef.current
      const pendingPatch = coordinator.getPending()
      commitRuntimeState({
        ...currentState,
        isPersisting: state.isPersisting,
        pendingPatch: hasBrandPatchKeys(pendingPatch)
          ? pendingPatch
          : state.isPersisting
            ? currentState.pendingPatch
            : {},
      })
    })
    return unsubscribe
  }, [coordinator, commitRuntimeState])

  // Brand token application is handled entirely in this effect, keyed on auth state.
  // We intentionally avoid any pre-auth application — brand tokens must only come
  // from a verified auth session, never from a stale localStorage cache that could
  // leak a previous org's branding onto the login page.
  useEffect(() => {
    if (!authLoading && user) {
      const settings = normalizeBrandSettings(getUserBrandSnapshot(user))
      const incomingRevision = getBrandRevision(settings)
      const identity = typeof user.id === 'string' ? user.id : null
      const isNewIdentity = authIdentityRef.current !== identity
      brandScopeRef.current = deriveBrandScope(user.featureFlags)

      if (isNewIdentity || shouldApplyServerSnapshot(incomingRevision, 'profile')) {
        const currentState = runtimeStateRef.current
        commitRuntimeState({
          ...currentState,
          effectiveSnapshot: settings,
          serverRevision: incomingRevision ?? currentState.serverRevision,
          localMutationEpoch: isNewIdentity ? 0 : currentState.localMutationEpoch,
          pendingPatch: isNewIdentity ? {} : currentState.pendingPatch,
          failedPatch: isNewIdentity ? null : currentState.failedPatch,
          failedError: isNewIdentity ? null : currentState.failedError,
        })
        runBrandSideEffects(settings, isNewIdentity ? 'auth-profile-new-identity' : 'auth-profile')
      }

      authIdentityRef.current = identity
      setIsLoading(false)
      return
    }

    if (!authLoading && !user) {
      previewDisposerRef.current?.()
      previewDisposerRef.current = null
      authIdentityRef.current = undefined
      commitRuntimeState(createInitialRuntimeState())
      clearBrandCache()
      resetThemeBrandTokens()
      syncThemeProviderFromBrandSettings(null, null)
      setIsLoading(false)
    }
  }, [
    user,
    authLoading,
    commitRuntimeState,
    runBrandSideEffects,
    shouldApplyServerSnapshot,
    syncThemeProviderFromBrandSettings,
  ])

  useEffect(() => {
    if (previousResolvedThemeRef.current === resolvedTheme) {
      return
    }

    previousResolvedThemeRef.current = resolvedTheme

    if (!authLoading && user) {
      runBrandSideEffects(runtimeStateRef.current.effectiveSnapshot, 'theme-change')
    }
  }, [resolvedTheme, authLoading, user, runBrandSideEffects])

  const updateBrandSettings = useCallback(
    async (newSettings: Partial<BrandSettings>, opts?: { immediate?: boolean }) => {
      const normalizedPatch = normalizeBrandPatch(newSettings)
      const currentState = runtimeStateRef.current
      const nextEpoch = currentState.localMutationEpoch + 1
      const nextSnapshot = normalizeBrandSettings(
        mergeBrandPatch(currentState.effectiveSnapshot, normalizedPatch) as BrandSettings,
      )
      const nextPendingPatch = mergeBrandPatch(currentState.pendingPatch, normalizedPatch)

      commitRuntimeState({
        ...currentState,
        effectiveSnapshot: nextSnapshot,
        localMutationEpoch: nextEpoch,
        pendingPatch: nextPendingPatch,
        failedPatch: null,
        failedError: null,
        isPersisting: true,
      })
      runBrandSideEffects(nextSnapshot, `local-mutation-${nextEpoch}`)

      try {
        return await coordinator.enqueue(normalizedPatch, opts)
      } catch (error) {
        const failedState = runtimeStateRef.current
        commitRuntimeState({
          ...failedState,
          failedPatch: normalizedPatch,
          failedError: getErrorMessage(error),
          isPersisting: coordinator.getState().isPersisting,
        })
        throw error
      }
    },
    [commitRuntimeState, coordinator, runBrandSideEffects],
  )

  const flushBrandSettings = useCallback(async () => {
    await coordinator.flush()
  }, [coordinator])

  const retryFailedBrandSettings = useCallback(async () => {
    const failedPatch = runtimeStateRef.current.failedPatch
    if (!failedPatch || !hasBrandPatchKeys(failedPatch)) {
      return null
    }

    return updateBrandSettings(failedPatch, { immediate: true })
  }, [updateBrandSettings])

  const resetToDefaults = useCallback(async () => {
    // Strip server-computed fields (brandRoleGraph, brandRoleGraphVersion, brandRoleGraphHash)
    // which are not accepted by the UpdateBrandSettingsDto — the backend recomputes them.
    const { brandRoleGraph: _rg, brandRoleGraphVersion: _rv, brandRoleGraphHash: _rh, ...writableDefaults } = getFairgenTwinsDefaultBrandSettings()
    // Reset path is "immediate" — bypass debounce so the next-cycle apply lands fast.
    await updateBrandSettings(writableDefaults, { immediate: true })
  }, [updateBrandSettings])

  // Visibility / unload flush: when the tab is hidden or about to unload,
  // ensure pending edits are persisted. Route-level flush is handled by
  // BrandTab on unmount (route changes unmount the page tab).
  //
  // Canonical guarantee path: `visibilitychange → hidden` — fires for tab
  // switches, app switches, and (on most browsers) hard navigations before
  // unload. The browser does not abort fetches when visibility changes, so
  // the in-flight save is allowed to complete.
  //
  // Best-effort path: `beforeunload` — included as a defense-in-depth fallback
  // for browsers/scenarios where visibilitychange does not fire (older Safari,
  // some embedded webviews). The browser does NOT wait for async work in this
  // handler, so the flush is fire-and-forget; in-flight HTTP requests may be
  // cancelled on real unload. Do not rely on this path for correctness.
  //
  // Additional best-effort path: `pagehide` — covers bfcache navigation and
  // mobile browser page lifecycle transitions where beforeunload is skipped.
  useEffect(() => {
    if (typeof document === 'undefined') return
    const visibilityHandler = () => {
      if (document.visibilityState === 'hidden') {
        void coordinator.flush()
      }
    }
    const pageExitHandler = () => {
      // Best-effort only — see comment above.
      void coordinator.flush()
    }
    document.addEventListener('visibilitychange', visibilityHandler)
    window.addEventListener('beforeunload', pageExitHandler)
    window.addEventListener('pagehide', pageExitHandler)
    return () => {
      document.removeEventListener('visibilitychange', visibilityHandler)
      window.removeEventListener('beforeunload', pageExitHandler)
      window.removeEventListener('pagehide', pageExitHandler)
    }
  }, [coordinator])

  // Provider unmount flush.
  useEffect(() => {
    return () => {
      previewDisposerRef.current?.()
      previewDisposerRef.current = null
      void coordinator.flush()
    }
  }, [coordinator])

  return (
    <BrandContext.Provider
      value={{
        brandSettings,
        isLoading,
        isPersisting: runtimeState.isPersisting,
        brandLogoUrl: brandSettings.brandLogoUrl || null,
        brandLogoUrlOnDark: brandSettings.brandLogoUrlOnDark || null,
        brandIconLogoUrl: brandSettings.brandIconLogoUrl || null,
        updateBrandSettings,
        previewBrandSettings,
        clearBrandSettingsPreview,
        flushBrandSettings,
        failedBrandPatch: runtimeState.failedPatch,
        brandPersistenceError: runtimeState.failedError,
        retryFailedBrandSettings,
        resetToDefaults,
      }}
    >
      {children}
    </BrandContext.Provider>
  )
}

export function useBrand() {
  const context = useContext(BrandContext)
  if (context === undefined) {
    throw new Error('useBrand must be used within a BrandProvider')
  }
  return context
}

/** Returns brand context or undefined when not in BrandProvider. Use for optional theming (e.g. PDF export). */
export function useBrandOptional(): BrandContextType | undefined {
  return useContext(BrandContext)
}

export function useIsAppBranded(): boolean {
  const { user } = useAuth()
  const { brandSettings } = useBrand()
  // Only full-scope (whiteLabel) constitutes an app-branded experience.
  return isAppBrandedExperience(user?.featureFlags?.whiteLabel === true, brandSettings)
}
